import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  MapPin, Camera, CheckCircle, Clock, Award, Eye, Users,
  MessageCircle, ChevronRight, Shield, Calendar, TrendingUp,
  Building2, Linkedin, Play, Sparkles
} from "lucide-react";
import { ContactSection, SectionDivider } from "@/Layout";
import { WA_LINK, CALENDLY } from "@/Layout";
import { WhatsAppBookingForm } from "@/components/WhatsAppBookingForm";
import { CrossSellingGift, CrossSellingProducts } from "@/components/CrossSelling";
import { TShirtBundle } from "@/components/TShirtBundle";
import { ConfrontoPrezziSection } from "@/components/ConfrontoPrezziSection";

// Virtual Tour Main Section
const VirtualTourSection = () => {
  const portfolio = [
    { name: "Open Studio", image: "https://lh3.googleusercontent.com/pw/AP1GczNH_iDl6CjdaFxtYvOPTlc9drJTLycSzz_UltrlEvezGR7VyYRwTE9LSywJTeJQDEAELLSX4aRwjmC4MITAY7krtmYZoQovFS1V3qQ93lyGV5gzSeMK=w800-h400-no", type: "Studio" },
    { name: "Poggio alle Lame", image: "https://lh3.googleusercontent.com/pw/AP1GczPv9Rr8KS1A-8nRipqV6Xhfbx0xJeSqBIpmNbO1gvOucpFVeobIVwuK78B-0ilZ72MZUYqhKxGEQE_txDp4ku3jojpVYyT685mHSidAQgws80o8--xb=w800-h400-no", type: "Agriturismo" },
    { name: "Quattropuntozero", image: "https://lh3.googleusercontent.com/pw/AP1GczN_0D2frmiV9WS41RNSEhquWFwTA6dG-BPSQYj8n8G57GgS0isNdBoIDoGvJtEKRSmT3uGqDYQQQ4eQVp7zqdIkRrng0pUFWvH-5ALCEgFYuO8XPTwn=w800-h400-no", type: "Locale" },
    { name: "Centro Medico Arnolfo", image: "https://lh3.googleusercontent.com/pw/AP1GczM1ChmyPTVlm6b93mXdQPYC8b9XrE83KEehiDgATIz5l-m2JHAls9r1NA3p8KjtbpHaGLqx3bJYQV2eJ0nmU_qL1BWifRfwqrhju7tlMQqkmWE2OU4w=w800-h400-no", type: "Centro Medico" },
  ];

  return (
    <section className="py-16 bg-[#0a0a0a] text-white relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#D4AF37] via-[#F5E6A3] to-[#D4AF37]"></div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back link */}
        <Link to="/" className="inline-flex items-center gap-2 text-gray-300 hover:text-white mb-8 transition-colors" data-testid="back-home">
          <ChevronRight className="w-4 h-4 rotate-180" /> Torna alle 4 porte
        </Link>

        <div className="text-center mb-12">
          {/* GoPro MAX Logo */}
          <div className="inline-block mb-5 relative" data-testid="gopro-channel-logo">
            <div className="bg-gray-900 border-2 border-[#D4AF37]/30 rounded-2xl p-3 shadow-xl shadow-[#D4AF37]/5">
              <img src="https://customer-assets.emergentagent.com/job_9aee3f9a-9ba9-47c5-9ef1-87f76cda4250/artifacts/b4icib11_image.png"
                alt="GoPro MAX 360" className="w-20 h-20 md:w-24 md:h-24 object-contain" />
            </div>
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-black text-[9px] font-black px-3 py-0.5 rounded-full tracking-widest uppercase">
              Powered by
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl font-black text-white mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
            <span style={{ background: 'linear-gradient(135deg, #D4AF37, #F5E6A3, #D4AF37, #B8942E)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Virtual Tour</span> 360
          </h1>
          <p className="text-gray-400 text-sm tracking-widest uppercase mb-2">immersive experience</p>
          <Badge className="bg-white/10 text-[#D4AF37] border-[#D4AF37]/40 mb-4">
            <Shield className="w-3 h-3 mr-1" /> Pacchetto Start INCLUSO
          </Badge>
          <p className="text-gray-300 max-w-2xl mx-auto">
            Fai <strong className="text-white">scoprire</strong> la <strong className="text-white">TUA</strong> attivita'
            ai clienti prima ancora che varchino la soglia. Un'esperienza immersiva e <strong className="text-[#D4AF37]">sorprendente</strong>.
          </p>
        </div>

        {/* Behind the Scenes */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <div className="relative rounded-2xl overflow-hidden aspect-[4/3] group" data-testid="vt360-action-photo">
            <img src="https://customer-assets.emergentagent.com/job_031c0675-bd53-49c3-b1d2-b66f6ab51727/artifacts/hke4h6ow_WhatsApp%20Image%202026-03-18%20at%2012.46.35.jpeg" alt="GoPro MAX 360 - La camera usata da Riccardo Rossi" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <h3 className="text-white font-bold text-xl mb-1">GoPro MAX 360</h3>
              <p className="text-gray-300 text-sm">16.6MP · 5.6K30 · HyperSmooth · La camera che uso per i tuoi tour</p>
            </div>
          </div>
          <div className="flex flex-col justify-center">
            <div className="bg-gray-800/50 backdrop-blur rounded-2xl p-6 border border-[#D4AF37]/20">
              <h3 className="text-2xl font-black text-white mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>L'attrezzatura fa la differenza</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3 border border-[#D4AF37]/30 rounded-lg px-3 py-2 bg-[#D4AF37]/5">
                  <div className="w-10 h-10 bg-gray-800/20 rounded-xl flex items-center justify-center flex-shrink-0"><Camera className="w-5 h-5 text-[#D4AF37]" /></div>
                  <div><p className="text-white font-bold text-sm">GoPro MAX 360</p><p className="text-gray-400 text-xs">Cattura ogni angolo della <strong className="text-gray-300">TUA</strong> attivita' in qualita' cinematografica</p></div>
                </div>
                <div className="flex items-start gap-3 border border-[#D4AF37]/30 rounded-lg px-3 py-2 bg-[#D4AF37]/5">
                  <div className="w-10 h-10 bg-gray-800/20 rounded-xl flex items-center justify-center flex-shrink-0"><Eye className="w-5 h-5 text-[#D4AF37]" /></div>
                  <div><p className="text-white font-bold text-sm">Esperienza Immersiva</p><p className="text-gray-400 text-xs">I <strong className="text-gray-300">TUOI</strong> clienti "entrano" nel locale — <strong className="text-gray-300">risultati sorprendenti</strong></p></div>
                </div>
                <div className="flex items-start gap-3 border border-[#D4AF37]/30 rounded-lg px-3 py-2 bg-[#D4AF37]/5">
                  <div className="w-10 h-10 bg-gray-800/20 rounded-xl flex items-center justify-center flex-shrink-0"><TrendingUp className="w-5 h-5 text-[#D4AF37]" /></div>
                  <div><p className="text-white font-bold text-sm">Pubblicazione su Google Street View</p><p className="text-gray-400 text-xs">La <strong className="text-gray-300">TUA</strong> attivita' visibile a milioni di persone su Google Maps</p></div>
                </div>
                <div className="flex items-start gap-3 border border-[#D4AF37]/30 rounded-lg px-3 py-2 bg-[#D4AF37]/5">
                  <div className="w-10 h-10 bg-[#ff6b35]/20 rounded-xl flex items-center justify-center flex-shrink-0"><Camera className="w-5 h-5 text-[#ff6b35]" /></div>
                  <div><p className="text-[#ff6b35] font-bold text-sm">3 foto sferiche/immersive in REGALO</p><p className="text-gray-400 text-xs">Incluse con ogni pacchetto — <strong className="text-[#ff6b35]">valorizza</strong> i tuoi spazi</p></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Portfolio Gallery */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="vt360-gallery">
          {portfolio.map((item, i) => (
            <div key={i} className="group relative rounded-2xl overflow-hidden aspect-[4/3] cursor-pointer">
              <img src={item.image} alt={`Virtual Tour ${item.name}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <Badge className="bg-gray-800/80 text-white border-0 text-xs mb-1">{item.type}</Badge>
                <p className="text-white font-bold text-sm">{item.name}</p>
              </div>
              <div className="absolute top-3 right-3 w-8 h-8 bg-[#D4AF37]/80 rounded-full flex items-center justify-center">
                <span className="text-black text-xs font-black">360</span>
              </div>
            </div>
          ))}
        </div>

        {/* Pricing comparison */}
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          <div className="bg-gray-800/50 backdrop-blur rounded-2xl p-6 border border-gray-700">
            <p className="text-gray-400 text-sm mb-2">Da un altro fotografo:</p>
            <div className="space-y-3">
              <div className="flex justify-between"><span className="text-gray-300">Piccola attivita'</span><span className="text-red-400 font-bold">da €250 a €350</span></div>
              <div className="flex justify-between"><span className="text-gray-300">Media attivita'</span><span className="text-red-400 font-bold">da €380 a €600</span></div>
              <div className="flex justify-between"><span className="text-gray-300">Hotel / Resort</span><span className="text-red-400 font-bold">da €600 a €2.000+</span></div>
              <div className="flex justify-between pt-3 border-t border-gray-600"><span className="text-gray-300">Richiesta esterna Virtual Tour</span><span className="text-red-400 font-bold text-xl">da €1.500</span></div>
            </div>
          </div>
          <div className="bg-gray-800/50 backdrop-blur rounded-2xl p-6 border-2 border-[#D4AF37]/40 relative">
            <div className="absolute -top-3 right-4 bg-[#4285F4] text-white px-4 py-1 rounded-full text-xs font-bold">START INCLUSO</div>
            <div className="text-center py-4">
              <p className="text-5xl font-bold text-white mb-1">€499</p>
              <p className="text-gray-300 text-sm font-medium">oppure €41,58/mese x12</p>
              <div className="mt-3 bg-[#4285F4]/20 border border-[#4285F4]/30 rounded-lg p-2">
                <p className="text-blue-200 text-xs font-bold">Pacchetto Start (€99) GIA' INCLUSO!</p>
                <p className="text-blue-300/70 text-xs mt-0.5">Verifica, claim e correzione scheda compresi</p>
              </div>
              <p className="text-gray-400 text-xs mt-2">Virtual Tour + Start = un unico investimento</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button className="bg-[#FFD814] hover:bg-[#F7CA00] text-gray-900 px-8 py-6 rounded-full font-bold text-lg border border-[#FCD200] shadow-md relative overflow-hidden group" onClick={() => window.open('https://www.paypal.me/Riccarossi/499', '_blank')} data-testid="vt360-cta-btn">
            <span className="relative z-10 inline-flex items-center justify-center gap-2">
              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.771.771 0 0 1 .761-.652h6.199c2.063 0 3.543.585 4.268 1.694.335.516.538 1.087.617 1.742.083.677.032 1.478-.157 2.398l-.013.063v.564l.44.249c.372.21.67.455.893.735.37.464.607 1.042.7 1.715.096.693.037 1.498-.173 2.393-.241 1.028-.635 1.922-1.171 2.655a5.292 5.292 0 0 1-1.539 1.546c-.593.39-1.298.672-2.097.84-.769.161-1.64.245-2.582.245h-.614a1.793 1.793 0 0 0-1.77 1.513l-.046.244-.77 4.88-.036.174a.164.164 0 0 1-.047.1.13.13 0 0 1-.078.028z" fill="#253B80"/><path d="M18.36 7.667a7.37 7.37 0 0 0-.078.449c-1 5.155-4.426 6.933-8.801 6.933h-2.228a1.08 1.08 0 0 0-1.07.918L5.09 22.83l-.323 2.054a.57.57 0 0 0 .563.66h3.952a.95.95 0 0 0 .94-.803l.038-.201.744-4.728.048-.26a.951.951 0 0 1 .94-.804h.591c3.83 0 6.828-1.556 7.704-6.063.366-1.882.176-3.453-.793-4.558a3.793 3.793 0 0 0-1.082-.835z" fill="#179BD7"/></svg>
              <span className="cta-wave-text">Paga con PayPal — €499</span>
            </span>
            <span className="cta-talpa-shine"></span>
          </Button>
          <div className="flex items-center justify-center gap-2 mt-3">
            <svg className="h-4 w-auto" viewBox="0 0 101 32" fill="#003087"><path d="M12.2 4.6h-7c-.5 0-.9.3-1 .8L1.5 24c-.1.4.2.7.6.7h3.4c.5 0 .9-.3 1-.8l.8-5.1c.1-.5.5-.8 1-.8h2.3c4.7 0 7.5-2.3 8.2-6.8.3-2 0-3.5-.9-4.6C16.8 5.3 14.8 4.6 12.2 4.6z"/><path d="M39.6 4.6h-7c-.5 0-.9.3-1 .8l-2.7 18.6c-.1.4.2.7.6.7h3.6c.3 0 .6-.2.7-.6l.8-5.3c.1-.5.5-.8 1-.8h2.3c4.7 0 7.5-2.3 8.2-6.8.3-2 0-3.5-.9-4.6C44.2 5.3 42.2 4.6 39.6 4.6z"/></svg>
            <span className="text-sm text-gray-400">oppure <strong className="text-white">€166,33 x 3 rate</strong> · TAEG 0%</span>
          </div>
          <p className="text-gray-300 text-xs mt-2">Pacchetto Start (€99) GIA' INCLUSO!</p>
          <div className="mt-6 flex justify-center gap-4">
            <Link to="/plus" className="text-sm text-gray-300 hover:text-white transition-colors">Plus (€299) per sospensioni e recensioni</Link>
            <Link to="/rooftop" className="text-sm text-gray-300 hover:text-white transition-colors">Rooftop (€599) = TUTTO + assistenza premium</Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// Prossimi Progetti — 3 Placeholder immersivi
const ProssimiProgettiSection = () => {
  const projects = [
    {
      title: "Cantina Storica del Chianti",
      subtitle: "Esperienza Immersiva tra le Botti",
      image: "https://images.unsplash.com/photo-1699393890422-b2022aec0b54?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHx3aW5lJTIwY2VsbGFyJTIwdmluZXlhcmQlMjB0dXNjYW55JTIwdW5kZXJncm91bmR8ZW58MHx8fHwxNzc1NDE3MDQ2fDA&ixlib=rb-4.1.0&q=85",
      desc: "Un viaggio a 360 gradi nel cuore della tradizione vinicola toscana. Botti in rovere, volte in mattoni e il profumo del Sangiovese che si sente attraverso lo schermo.",
      tag: "IN ARRIVO",
      color: "#EA4335",
      timewarp: false,
    },
    {
      title: "Boutique Hotel — Relais Toscano",
      subtitle: "Dalla Hall alla Suite Panoramica",
      image: "https://images.unsplash.com/photo-1721852474658-884e1881fe31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxib3V0aXF1ZSUyMGhvdGVsJTIwc3BhJTIwbHV4dXJ5JTIwaW50ZXJpb3IlMjB0dXNjYW55fGVufDB8fHx8MTc3NTQxNzAzNXww&ixlib=rb-4.1.0&q=85",
      desc: "Un tour completo dell'ospitalita' toscana: dalla reception al giardino segreto, ogni stanza racconta una storia. Il cliente prenota dopo aver 'visitato' la suite.",
      tag: "IN ARRIVO",
      color: "#34A853",
      timewarp: false,
    },
    {
      title: "Ristorante d'Autore — Timewarp",
      subtitle: "Dal Giorno alla Sera in 30 Secondi",
      image: "https://images.unsplash.com/photo-1760611655728-f5f0279ed611?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjByZXN0YXVyYW50JTIwaW50ZXJpb3IlMjBwaG90b2dyYXBoeSUyMGVsZWdhbnR8ZW58MHx8fHwxNzc1NDE3MDMwfDA&ixlib=rb-4.1.0&q=85",
      desc: "La magia del Timewarp: lo stesso ristorante fotografato di giorno e trasformato nella versione serale con candele e luci soffuse. Un effetto cinematografico che lascia senza fiato.",
      tag: "TIMEWARP",
      color: "#D4AF37",
      timewarp: true,
    },
  ];

  return (
    <section className="py-20 bg-[#080c18] relative overflow-hidden" data-testid="prossimi-progetti-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.05)_0%,_transparent_60%)]"></div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 text-[#D4AF37] px-4 py-1.5 rounded-full font-bold text-xs mb-4 border border-[#D4AF37]/20">
            <Sparkles className="w-4 h-4" /> PROSSIMI PROGETTI
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-white mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
            I prossimi Virtual Tour in <span style={{ background: 'linear-gradient(135deg, #D4AF37, #F5E6A3, #D4AF37)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>lavorazione</span>
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto">
            Tre progetti unici che dimostrano la versatilita' del Virtual Tour 360. Ogni spazio racconta una storia diversa.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {projects.map((p, i) => (
            <div
              key={i}
              className="group relative rounded-2xl overflow-hidden bg-white/[0.03] border border-white/10 hover:border-[#D4AF37]/40 transition-all duration-500"
              data-testid={`progetto-${i + 1}`}
            >
              {/* Image */}
              <div className="relative aspect-[16/10] overflow-hidden">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                {/* Badge */}
                <div className="absolute top-3 left-3">
                  <span
                    className="text-[10px] font-black px-3 py-1 rounded-full tracking-wider uppercase"
                    style={{ backgroundColor: p.color, color: p.timewarp ? '#000' : '#fff' }}
                  >
                    {p.tag}
                  </span>
                </div>
                {/* 360 indicator */}
                <div className="absolute top-3 right-3 w-10 h-10 bg-black/60 backdrop-blur rounded-full flex items-center justify-center border border-white/20">
                  <span className="text-white text-[10px] font-black">360</span>
                </div>
                {/* Play overlay */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30">
                    <Play className="w-6 h-6 text-white ml-0.5" />
                  </div>
                </div>
              </div>
              {/* Content */}
              <div className="p-5">
                <h3 className="text-lg font-black text-white mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>{p.title}</h3>
                <p className="text-sm font-medium mb-3" style={{ color: p.color }}>{p.subtitle}</p>
                <p className="text-gray-400 text-xs leading-relaxed">{p.desc}</p>
                {p.timewarp && (
                  <div className="mt-3 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-lg px-3 py-2">
                    <p className="text-[#D4AF37] text-[10px] font-black uppercase tracking-wider">Effetto Timewarp</p>
                    <p className="text-gray-400 text-[10px]">Due riprese, stesso luogo, due atmosfere. Magia pura.</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B8942E] text-black font-bold px-6 py-3 rounded-full transition-colors text-sm"
            data-testid="cta-prossimi-progetti"
          >
            <MessageCircle className="w-4 h-4" />
            Vuoi il tuo Virtual Tour? Scrivimi su WhatsApp
          </a>
          <p className="text-gray-500 text-xs mt-3">Ogni progetto e' unico — parliamone insieme</p>
        </div>
      </div>
    </section>
  );
};

// Certified Photographer Section
const CertifiedPhotographerSection = () => {
  const services = [
    { icon: Camera, title: "Tour Virtuali a 360", desc: "Tour virtuali immersivi che permettono ai tuoi clienti di esplorare i tuoi spazi." },
    { icon: Eye, title: "Fotografia Professionale", desc: "Fotografie di alta qualita' ottimizzate per Google Maps e Search." },
    { icon: MapPin, title: "Ottimizzazione Google Maps", desc: "Immagini e tour perfettamente visibili su Google Maps e Search." },
    { icon: Shield, title: "Certificazione Ufficiale", desc: "Professionista certificato da Google con competenze dimostrate." },
    { icon: Users, title: "Supporto Strategico", desc: "Collaboro con te per capire le tue esigenze e migliorare la presenza online." },
    { icon: Clock, title: "Aggiornamenti Periodici", desc: "Aggiornamenti delle immagini e tour per riflettere cambiamenti." },
  ];

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
            Cerchi qualcuno che si occupi del <strong>TUO</strong> <span className="text-[#ff6b35]">Business Profile</span>?
          </h2>
          <div className="bg-gray-900 text-white px-8 py-6 rounded-2xl max-w-3xl mx-auto mb-8 shadow-xl">
            <p className="text-xl md:text-2xl font-medium leading-relaxed">
              <strong>Scopri</strong> qualcuno in <span className="text-[#ff6b35] font-bold">Valdelsa o Siena</span>,
              in carne ed ossa, che ti offre <strong>risultati</strong> in <span className="text-green-400 font-bold">72 ore</span>
            </p>
          </div>
          <h3 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">Fotografo Google Street View <span className="text-green-600">Certificato</span></h3>
        </div>

        <div className="flex flex-col lg:flex-row items-center gap-12 mb-16">
          <div className="lg:w-1/3">
            <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-3xl shadow-xl border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">Le Mie Certificazioni</h3>
              <div className="flex flex-col gap-6 items-center">
                <div className="bg-white p-4 rounded-2xl shadow-lg border border-gray-100">
                  <img src="https://customer-assets.emergentagent.com/job_sos-activity-cards/artifacts/wr288sey_Google-Street-View-Trusted-Badge-2021.png" alt="Street View Trusted" className="h-32 w-auto object-contain" />
                  <p className="text-center text-sm font-medium text-gray-700 mt-3">Street View Trusted</p>
                </div>
                <div className="bg-white p-4 rounded-2xl shadow-lg border border-gray-100">
                  <img src="https://customer-assets.emergentagent.com/job_sos-activity-cards/artifacts/eknv50vh_adesivogoogle2.jpg" alt="Fotografo Certificato" className="h-32 w-auto object-contain" />
                  <p className="text-center text-sm font-medium text-gray-700 mt-3">Fotografo Certificato</p>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:w-2/3">
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-xl">
              <CardContent className="p-8">
                <div className="flex items-start gap-6">
                  <div className="hidden md:block"><div className="w-24 h-24 bg-green-500 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-lg">RR</div></div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Riccardo Rossi</h3>
                    <p className="text-green-600 font-semibold mb-4">Fotografo Google Street View Certificato | Siena</p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Sono l'<strong>unico professionista certificato Google Street View</strong> operativo a Siena e provincia.
                      La certificazione garantisce che le immagini rispettano gli <strong>standard qualitativi di Google</strong>.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <Badge className="bg-green-100 text-green-700 border-green-300 px-3 py-1"><CheckCircle className="w-4 h-4 mr-1" />Certificato Google</Badge>
                      <Badge className="bg-green-100 text-green-700 border-green-300 px-3 py-1"><MapPin className="w-4 h-4 mr-1" />Zona Siena</Badge>
                      <Badge className="bg-green-100 text-green-700 border-green-300 px-3 py-1"><Camera className="w-4 h-4 mr-1" />Tour 360</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Cosa Fa un Fotografo Google Street View Certificato?</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="bg-[#faf8f5] border-gray-200 hover:border-green-500/50 transition-all group">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-green-500/20"><service.icon className="w-6 h-6 text-green-600" /></div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">{service.title}</h4>
                  <p className="text-gray-600 text-sm">{service.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const stats = [
    { icon: Camera, value: "10.000+", label: "Foto Scattate", description: "Molte a 360 come l'auto ufficiale Google", color: "text-red-500" },
    { icon: Eye, value: "52 MILIONI", label: "Visualizzazioni", description: "Persone aiutate a trovare la strada", color: "text-yellow-500" },
    { icon: Building2, value: "50+", label: "Aziende Aiutate", description: "Tutte con recensioni a 5 stelle", color: "text-green-500" },
  ];
  return (
    <section className="py-20 bg-[#faf8f5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <Badge className="bg-[#ff6b35]/20 text-[#ff6b35] border-[#ff6b35]/30 mb-4">Google Maps Local Guide</Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">I Numeri Parlano da Soli</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">Come fotografo certificato Google a Siena, aiuto le persone a trovare la location giusta</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {stats.map((stat, index) => (
            <Card key={index} className="bg-white border-gray-200 hover:border-[#ff6b35]/50 transition-all shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#ff6b35]/10 rounded-2xl flex items-center justify-center mx-auto mb-6"><stat.icon className={`w-8 h-8 ${stat.color}`} /></div>
                <div className={`text-4xl font-bold ${stat.color} mb-2`}>{stat.value}</div>
                <div className="text-lg font-semibold text-gray-900 mb-2">{stat.label}</div>
                <p className="text-gray-500 text-sm">{stat.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
        <Card className="bg-gradient-to-r from-[#ff6b35]/10 to-orange-100 border-[#ff6b35]/30">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Digitale = Reale</h3>
                <p className="text-gray-600 leading-relaxed">
                  Il mio lavoro e' <strong className="text-[#ff6b35]">allineare il mondo digitale con quello reale</strong>,
                  cosi' i tuoi clienti a Siena e provincia ti trovano sempre nel posto giusto.
                </p>
              </div>
              <div className="flex gap-4">
                <Button className="bg-[#ff6b35] hover:bg-[#e55a2b] rounded-full" onClick={() => window.open('https://maps.app.goo.gl/TsgkEsg6WWvqE6x5A?g_st=aw', '_blank')}>
                  <MapPin className="w-4 h-4 mr-2" /> Vedi il Mio Profilo
                </Button>
                <Button variant="outline" className="border-[#ff6b35] text-[#ff6b35] hover:bg-[#ff6b35] hover:text-white rounded-full" onClick={() => window.open('https://www.linkedin.com/in/riccardorossi1984googletnx/', '_blank')}>
                  <Linkedin className="w-4 h-4 mr-2" /> LinkedIn
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default function VirtualTourPage() {
  return (
    <>
      <VirtualTourSection />
      <ProssimiProgettiSection />
      <ConfrontoPrezziSection />
      <CertifiedPhotographerSection />
      <SectionDivider />
      <StatsSection />
      <WhatsAppBookingForm pacchetto="Virtual Tour 360" color="#D4AF37" />
      <TShirtBundle pacchetto="Virtual Tour" />
      <CrossSellingGift pacchetto="Virtual Tour 360" />
      <CrossSellingProducts pacchetto="Virtual Tour 360" />
      <ContactSection />
    </>
  );
}
